import { useState, type ReactNode } from "react";
import { Alert, App, Button, Card, Checkbox, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { PolicyVersionDto } from "@audit-plan/shared";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { palette } from "../theme";

/**
 * Pagina a schermo intero con il testo dell'informativa e una checkbox obbligatoria: al primo accesso e a ogni nuova versione.
 * Il blocco vero sta nel backend (403 policy_acceptance_required): questa è la parte visibile. A regime i fornitori useranno il
 * Vendor Portal, che consuma le stesse rotte (GET /policies/pending, POST /policies/:id/accept).
 */
export function PolicyGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { logout } = useAuth();
  const qc = useQueryClient();
  const [ok, setOk] = useState(false);
  const pending = useQuery({ queryKey: ["policies-pending"], queryFn: () => api.get<{ items: PolicyVersionDto[] }>("/policies/pending").then((r) => r.items) });
  const accept = useMutation({
    mutationFn: (id: string) => api.post(`/policies/${id}/accept`, { accepted: true }),
    onSuccess: async () => {
      setOk(false);
      await qc.invalidateQueries({ queryKey: ["policies-pending"] });
      void qc.invalidateQueries();
    },
    onError: (e) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail")),
  });
  const p = pending.data?.[0];
  if (!p) return <>{children}</>;
  return (
    <div style={{ minHeight: "100dvh", background: palette.sunk, display: "grid", placeItems: "start center", padding: "24px 16px" }}>
      <Card style={{ width: "min(760px, 100%)" }} title={p.title}>
        {/BOZZA/i.test(p.body.slice(0, 40)) && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t("d_gateDraft")} />}
        <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>{t("d_gateIntro")}</Typography.Paragraph>
        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, maxHeight: "50dvh", overflowY: "auto", padding: 12, border: `1px solid ${palette.hair}`, borderRadius: 8, marginBottom: 16 }}>{p.body}</div>
        <Checkbox checked={ok} onChange={(e) => setOk(e.target.checked)}>{t("d_gateCheck")}</Checkbox>
        <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button type="primary" disabled={!ok} loading={accept.isPending} onClick={() => accept.mutate(p.id)}>{t("d_gateAccept")}</Button>
          <Button onClick={() => void logout()}>{t("logout")}</Button>
        </div>
      </Card>
    </div>
  );
}
